import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { SharingCard } from "@/components/sharing/SharingCard";

import { useFirebase } from "@components/firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { getAllShares, unshare, share } from "@/services/firebase/share-functions";
import { ShareModel } from "common247";
import { Spacing } from "@/constants/ThemeColours";

const SHARE_SORT_OPTIONS: ISortOption<ShareModel>[] = [
  { key: "sharedAt", label: "Date Shared", getValue: (s) => s.sharedAt || 0 },
  { key: "sourceDisplayName", label: "Item Name", getValue: (s) => s.sourceDisplayName || s.sourceId || "" },
  { key: "sharer", label: "Sharer Name", getValue: (s) => s.sharedByDisplayName || s.sharedBy || "" },
  { key: "targetDisplayName", label: "Recipient Name", getValue: (s) => s.targetDisplayName || s.targetId || "" },
  { key: "sourceType", label: "Item Type", getValue: (s) => s.sourceType || "" },
  { key: "role", label: "Permission Role", getValue: (s) => s.role || "" },
];

const SHARE_GROUP_OPTIONS: IGroupOption<ShareModel>[] = [
  {
    key: "sharer",
    label: "Sharer",
    getGroupValue: (s) => s.sharedByDisplayName || s.sharedBy || "System / Unknown Sharer",
  },
  {
    key: "sourceType",
    label: "Shared Item Type",
    getGroupValue: (s) => {
      const type = s.sourceType || "item";
      return type.charAt(0).toUpperCase() + type.slice(1) + " Shares";
    },
  },
  {
    key: "targetType",
    label: "Recipient Type",
    getGroupValue: (s) => (s.targetType === "contact" ? "Shared with Contacts" : "Shared with Groups"),
  },
  {
    key: "role",
    label: "Permission Role",
    getGroupValue: (s) => (s.role === "editor" ? "Editor Permissions" : "Viewer Permissions"),
  },
];

/**
 * Screen displaying all system-wide shares for administrators with view, sort, group, and filter capabilities.
 * Utilizes the shared SharingCard component to display resolved names for sharers, recipients, and items.
 */
export default function ShareAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [shares, setShares] = useState<ShareModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Fetches all shares across the platform with resolved entity and user display names.
   */
  const fetchSharesData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getAllShares(services, {});
      if (response.status) {
        setShares(response.shares || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to load shares.");
      }
    } catch (error) {
      console.error("Error fetching all shares:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching shares.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchSharesData();
    }, [fetchSharesData]),
  );

  /**
   * Revokes a share record after confirming with the administrator.
   */
  const handleRevokeShare = useCallback(
    (shareItem: ShareModel) => {
      if (!shareItem.id) {
        CustomAlert.alert("Error", "Cannot revoke a share without a valid identifier.");
        return;
      }

      const targetLabel = shareItem.targetDisplayName || shareItem.targetId || "this recipient";
      const sourceLabel = shareItem.sourceDisplayName || shareItem.sourceId || "this item";

      CustomAlert.alert(
        "Confirm Revocation",
        `Are you sure you want to revoke access to "${sourceLabel}" for ${targetLabel}?`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Revoke Access",
            style: "destructive",
            onPress: async () => {
              if (!services || !shareItem.id) return;
              try {
                const res = await unshare(services, { shareId: shareItem.id });
                if (res.status) {
                  setShares((prev) => prev.filter((s) => s.id !== shareItem.id));
                } else {
                  CustomAlert.alert("Error", res.error || "Failed to revoke share.");
                }
              } catch (err: any) {
                console.error("Error revoking share:", err);
                CustomAlert.alert("Error", err.message || "An unexpected error occurred.");
              }
            },
          },
        ],
      );
    },
    [services],
  );

  /**
   * Updates the permission role of a share record.
   */
  const handleRoleChange = useCallback(
    async (shareItem: ShareModel, newRole: "viewer" | "editor") => {
      if (!services || !shareItem.id) return;
      try {
        const res = await share(services, {
          sourceId: shareItem.sourceId,
          sourceType: shareItem.sourceType,
          targetId: shareItem.targetId,
          targetType: shareItem.targetType,
          role: newRole,
        });
        if (res.status) {
          setShares((prev) =>
            prev.map((s) => (s.id === shareItem.id ? { ...s, role: newRole } : s)),
          );
        } else {
          CustomAlert.alert("Error", res.error || "Failed to update share permission.");
        }
      } catch (err: any) {
        console.error("Error updating share role:", err);
        CustomAlert.alert("Error", err.message || "An unexpected error occurred.");
      }
    },
    [services],
  );

  return (
    <ThemedScreen title="All Shares" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${shares.length} TOTAL ${shares.length === 1 ? "SHARE" : "SHARES"}`}
        </ThemedText>

        <ThemedList<ShareModel>
          data={shares}
          sortOptions={SHARE_SORT_OPTIONS}
          defaultSortKey="sharedAt"
          defaultSortDirection="desc"
          groupOptions={SHARE_GROUP_OPTIONS}
          defaultGroupKey="sharer"
          persistenceKey="admin-shares"
          pageSize={24}
          filterKeys={["sourceDisplayName", "targetDisplayName", "sharedByDisplayName", "sourceType", "role"]}
          filterPlaceholder="Search all shares by item, recipient, sharer..."
          loading={loading}
          onRefresh={() => fetchSharesData(true)}
          refreshing={refreshing}
          emptyText="No shares found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, isHovered, viewMode) => {
            const sharer = item.sharedByDisplayName || item.sharedBy || "Unknown";
            const sourceName = item.sourceDisplayName || item.sourceId;
            const dateStr = item.sharedAt ? new Date(item.sharedAt).toLocaleDateString() : "";
            const subtitle = `${sourceName} (${item.sourceType}) • Shared by ${sharer}${dateStr ? ` • ${dateStr}` : ""}`;

            return (
              <SharingCard
                share={item}
                subtitle={subtitle}
                viewMode={viewMode}
                isHovered={isHovered}
                canShare={true}
                canUnshare={true}
                onRoleChange={(newRole) => handleRoleChange(item, newRole)}
                onUnshare={() => handleRevokeShare(item)}
              />
            );
          }}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 8,
    paddingTop: 6,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: Spacing.five,
  },
});
